import {
    toColumn,
    DEFAULT_COLUMN_BUILDER_ID,
    ColumnBuilder,
} from "@impero-com/common/components/dataTable";
import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import * as Cmp from "@impero-com/common/utils/comparison";
import {
    DataExtractor,
    ExtractorExpander,
    PartialData,
} from "@impero-com/common/utils/dataView";
import { CustomFilterTransformer } from "@impero-com/common/utils/dataView/extractor";
import { parseDate } from "@impero-com/common/utils/date";
import {
    formatFinancialYear,
    formatDate,
    formatUserName,
} from "@impero-com/common/utils/formatting";
import { t, TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { InfoTooltip, renderMarkdown, Tooltip } from "@impero-com/ui";

import {
    UserGroupId,
    ControlWithoutFieldsGraphItemDtoListDto as ControlWithoutFieldsListGraphDto,
    SchedulingType,
    SubsequentAssignmentKind,
    UserOverviewDtoContent,
    UserGroupViewDtoContent,
    Month,
    TwoMonths,
    Quarter,
    HalfYear,
    DayOfWeek,
    Recurrence,
    UserOrGroup,
    RecurrenceRuleWithOffsetForMonthAndDueDateOffsetForMonthOffsetPeriod as MonthlyRecurrenceRule,
    WeeklyRecurrenceRule,
    RecurrenceRuleWithOffsetForTwoMonthsAndDueDateOffsetForMonthOffsetPeriod as EveryTwoMonthsRecurrenceRule,
    RecurrenceRuleWithOffsetAndFinancialYearForQuarterAndDueDateOffsetForMonthOffsetPeriod as QuarterlyRecurrenceRule,
    RecurrenceRuleWithOffsetAndFinancialYearForHalfYearAndDueDateOffsetForMonthOffsetPeriod as HalfYearlyRecurrenceRule,
    DueDateOffsetForMonthOffsetPeriod as MonthDueDateOffset,
    FinancialYearView,
    StandardAccessRight,
    CalendarView,
    EntityView,
    UserTagOverviewListDto,
} from "api/generated/model";
import { getLocalizedAccessRightDescription } from "components/accessRights/localized";
import { TagList } from "components/tag/tagList";
import { TagsData } from "components/tag/types";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import {
    translateDayOfWeek,
    translateMonth,
    translateTwoMonthsPeriod,
    translateRecurrenceWeeklyPeriodicity,
    translateQuarter,
    translateHalfYear,
} from "org/control/list/utils";
import { formatControlSchedulingType } from "org/control/utils";
import { IdAndName } from "org/pageState/models";
import { compareAccessRight, formatAccessRight } from "utils/accessRights";
import { ControlProgramAccessRightsInstance } from "utils/accessRights/models";
import {
    ASSIGNEE_CATEGORY_METHODS,
    ASSIGNEE_TAG_CATEGORIES_ID,
} from "utils/assigneeTags";
import { ControlAssignmentGroup } from "utils/controls/assignmentUtils";
import {
    ASSIGNMENT_EXTRACTOR_KEYS,
    CTRL_EXTRACTOR_KEYS,
} from "utils/controls/extractorKeys";
import { downgradeExtractorType } from "utils/dataView";
import {
    createCategoryNameLookupFn,
    createTagNameLookupFn,
    DEFAULT_CATEGORY_METHODS,
    isTagCategoriesExtractorId,
    TAG_CATEGORIES_ID,
} from "utils/tag";

import { WebRoute } from "../../../../routes";
import {
    ControlLoginProtection,
    ControlProgramStatus,
    ControlSchedulingType,
    controlSchedulingTypeToSchedulingType,
    ControlSegregationOfDuties,
    formatControlLoginProtection,
    formatControlProgramStatus,
    formatControlSegregationOfDuties,
    schedulingTypeToControlSchedulingType,
    translateOffsetAnchor,
    translateOffsetType,
} from "../utils";

import { HoverableItemList } from "./common/hoverableItemList";
import * as theme from "./theme.css";
import { ControlDataRow } from "./types";

const CATEGORY_EXTRACTORS_ID = "categoryExtractors";
const ASSIGNEE_CATEGORY_EXTRACTORS_ID = "onlySearchAssigneeCategoryExtractors";
const MAX_DESCRIPTION_LENGTH = 100;

type SchedulingTypeOrAll = SchedulingType | "All";

type AllowedPeriodicityTKeys =
    | "CreateControl_Reccurence_Weekly_Periodicity_1"
    | "CreateControl_Reccurence_Weekly_Periodicity_2"
    | "CreateControl_Reccurence_Weekly_Periodicity_3"
    | "CreateControl_Reccurence_Weekly_Periodicity_4"
    | "CreateControl_Reccurence_Weekly_Periodicity_5"
    | "CreateControl_Reccurence_Weekly_Periodicity_6";

function isOnlySearchColumn(extractorId: string) {
    return extractorId.startsWith("onlySearch");
}

type PeriodEnding = "Month" | "TwoMonths" | "Quarter" | "Half" | "Year";
function translatePeriodEnding(ending: PeriodEnding): string {
    switch (ending) {
        case "Month":
            return t("CreateControl_Recurrence_DateRule_OfTheMonth");
        case "TwoMonths":
            return t("CreateControl_Recurrence_DateRule_OfTheTwoMonth");
        case "Quarter":
            return t("CreateControl_Recurrence_DateRule_OfTheQuarter");
        case "Half":
            return t("CreateControl_Recurrence_DateRule_OfTheHalf");
        case "Year":
            return t("CreateControl_Recurrence_DateRule_OfTheYear");
        default:
            return assertNever(ending);
    }
}

function formatRecurrenceCanonical(
    defaultDueDateOffset: MonthDueDateOffset,
    calendar: CalendarView | null | undefined,
    ending: PeriodEnding
): string {
    const months = defaultDueDateOffset.offsetPeriod.months;

    // NOTE: +1 offset is on purpose to display the number of days that were entered in the legacy form (it's not a bug).
    const days = defaultDueDateOffset.offsetPeriod.days + 1;

    const dayOrDays = t("Controls_Schedule_Day", { count: days });
    const monthOrMonths = t("Controls_Schedule_Month", { count: months });

    const calendarName = calendar ? calendar.name : dayOrDays;
    const beforeAfter = translateOffsetType(defaultDueDateOffset.offsetType);
    const anchor = translateOffsetAnchor(defaultDueDateOffset.anchor);
    const ofThePeriod = translatePeriodEnding(ending);
    const monthDisplay =
        months === 0
            ? ""
            : `${months} ${monthOrMonths} ${t(
                  "SavePushReportConfiguration_And"
              )} `;

    return `${monthDisplay}${days} ${calendarName} ${beforeAfter} ${t(
        "CreateControl_Recurrence_DateRule_TheAnchor"
    )} ${anchor} ${ofThePeriod}`;
}

function formatRecurrenceWithEnding(
    row: ControlDataRow,
    ending: PeriodEnding
): string {
    return formatRecurrenceCanonical(
        extractDefaultDueDateOffset(row),
        row.calendar,
        ending
    );
}

function extractDefaultDueDateOffset(row: ControlDataRow): MonthDueDateOffset {
    const control = row.control.resource;
    const scheduling = control.scheduling;

    if (!scheduling) {
        throw new Error(`Control (id=${control.id}) has no scheduling`);
    }
    const rule = scheduling.rule;

    switch (rule.type) {
        case "Monthly":
        case "EveryTwoMonths":
        case "Quarterly":
        case "HalfYearly":
        case "Yearly":
            return rule.defaultDueDateOffset;
        case "Date":
        case "Weekly":
            throw new Error(
                `ControlRecurrenceRule (type = ${rule.type}) has no defaultDueDateOffset property`
            );
        default:
            return assertNever(rule);
    }
}

interface AllControlColumns {
    [key: string]: DataExtractor<ControlDataRow, unknown>;
}

const allColumns: AllControlColumns = {};

// This is done because i18next doesn't have the translations loaded on file load
function createColumns(
    orgFinancialYear: FinancialYearView | null,
    calendars: CalendarView[],
    allTags: UserTagOverviewListDto,
    controls: ControlWithoutFieldsListGraphDto,
    usersById: Map<number, UserOverviewDtoContent>,
    userGroupById: Map<UserGroupId, UserGroupViewDtoContent>
) {
    if (allColumns.name) {
        return;
    }
    const columns: Array<DataExtractor<ControlDataRow, unknown>> = [
        downgradeExtractorType(buildNameExtractor(controls)),
        downgradeExtractorType(buildDescriptionExtractor(controls)),
        downgradeExtractorType(buildProgramExtractor(controls)),
        downgradeExtractorType(buildSchedulingTypeExtractor()),
        downgradeExtractorType(buildStatusExtractor()),
        {
            id: CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
            label: t("Assignment"),
            getValue: (row) => row.assignments,
            compare: Cmp.map((row) => row.assignments.length, Cmp.primitive),
            // Used in exporting to Excel
            asString: (row) => row.assignments.length.toString(),
        },
        downgradeExtractorType(buildLoginProtectionExtractor()),
        downgradeExtractorType(buildSegregationOfDutiesExtractor()),
        downgradeExtractorType(buildTagsExtractor(allTags)),
        downgradeExtractorType(buildTagCategoriesExtractor(allTags)),
        downgradeExtractorType(buildAccessRightExtractor()),
        downgradeExtractorType(buildNotificationExtractor()),
        downgradeExtractorType(buildFinancialYearExtractor(orgFinancialYear)),
        {
            id: CTRL_EXTRACTOR_KEYS.RECURRENCE_WEEKLY,
            label: t("CreateControl_Reccurence_TypeLabel"),
            getValue: (_row) => "",
            compare: (a, b) =>
                extractWeeklyRecurrenceRule(a).days.length -
                extractWeeklyRecurrenceRule(b).days.length,
            asString: formatWeeklyRecurrence,
        },
        downgradeExtractorType(buildOnlySearchRecurrenceWeeklyDaysExtractor()),
        downgradeExtractorType(
            buildOnlySearchRecurrenceWeeklyPeriodicityExtractor()
        ),
        downgradeExtractorType(buildScheduleMonthlyExtractor()),
        downgradeExtractorType(buildRecurrenceMonthlyExtractor(calendars)),
        downgradeExtractorType(
            buildRecurrenceEveryTwoMonthsExtractor(calendars)
        ),
        downgradeExtractorType(buildRecurrenceQuarterlyExtractor(calendars)),
        downgradeExtractorType(buildRecurrenceHalfYearlyExtractor(calendars)),
        downgradeExtractorType(buildRecurrenceYearlyExtractor(calendars)),
        downgradeExtractorType(buildScheduleEveryTwoMonthsExtractor()),
        downgradeExtractorType(buildScheduleQuarterlyExtractor()),
        downgradeExtractorType(buildScheduleHalfYearlyExtractor()),
        downgradeExtractorType(buildDueDateManualExtractor()),
        downgradeExtractorType(
            buildAssignmentsNameExtractor(
                ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ASSIGNEE,
                t("Control_Management_Assignee"),
                "assignee",
                usersById,
                userGroupById
            )
        ),
        downgradeExtractorType(
            buildAssignmentsNameExtractor(
                ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_FIRST_REVIEWER,
                t("Control_Management_FirstReviewer"),
                "first reviewer",
                usersById,
                userGroupById
            )
        ),
        downgradeExtractorType(
            buildAssignmentsNameExtractor(
                ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_SECOND_REVIEWER,
                t("Control_Management_SecondaryReviewer"),
                "second reviewer",
                usersById,
                userGroupById
            )
        ),
        downgradeExtractorType(buildAssigneeTagCategoriesExtractor(allTags)),
        downgradeExtractorType(buildControlResultsCountExtractor()),
        downgradeExtractorType(buildNextDueDateExtractor()),
        downgradeExtractorType(buildAssignmentsEntityExtractor()),
    ];
    columns.forEach((x) => (allColumns[x.id] = x));
}

function extractWeeklyRecurrenceRule(
    row: ControlDataRow
): WeeklyRecurrenceRule {
    const control = row.control.resource;
    if (control.scheduling?.rule?.type === "Weekly") {
        return control.scheduling.rule;
    } else {
        throw new Error(
            `Control(id=${control.id}) is expected to have Weekly recurrence rule`
        );
    }
}

function extractMonthlyRecurrenceRule(
    row: ControlDataRow
): MonthlyRecurrenceRule {
    const control = row.control.resource;
    if (control.scheduling?.rule?.type === "Monthly") {
        return control.scheduling.rule;
    } else {
        throw new Error(
            `Control(id=${control.id}) is expected to have Monthly recurrence rule`
        );
    }
}

function extractEveryTwoMonthsRecurrenceRule(
    row: ControlDataRow
): EveryTwoMonthsRecurrenceRule {
    const control = row.control.resource;
    if (control.scheduling?.rule?.type === "EveryTwoMonths") {
        return control.scheduling.rule;
    } else {
        throw new Error(
            `Control(id=${control.id}) is expected to have EveryTwoMonths recurrence rule`
        );
    }
}

function extractQuarterlyRecurrenceRule(
    row: ControlDataRow
): QuarterlyRecurrenceRule {
    const control = row.control.resource;
    if (control.scheduling?.rule?.type === "Quarterly") {
        return control.scheduling.rule;
    } else {
        throw new Error(
            `Control(id=${control.id}) is expected to have Quarterly recurrence rule`
        );
    }
}

function extractQuarterlyRecurrenceRuleQuarters(
    row: ControlDataRow
): Array<Quarter> {
    const rule = extractQuarterlyRecurrenceRule(row);
    // SAFETY: rule.occurrences is of type Record<Quarter, MonthDueDateOffset>.
    return Object.keys(rule.occurrences) as Array<Quarter>;
}

function extractMonthlyRecurrenceRuleMonths(row: ControlDataRow): Array<Month> {
    const rule = extractMonthlyRecurrenceRule(row);
    // SAFETY: rule.occurrences is of type Record<Month, MonthDueDateOffset>.
    return Object.keys(rule.occurrences) as Array<Month>;
}

function extractEveryTwoMonthsRecurrenceRulePeriods(
    row: ControlDataRow
): Array<TwoMonths> {
    const rule = extractEveryTwoMonthsRecurrenceRule(row);
    // SAFETY: rule.occurrences is of type Record<TwoMonths, MonthDueDateOffset>.
    return Object.keys(rule.occurrences) as Array<TwoMonths>;
}

function extractHalfYearlyRecurrenceRule(
    row: ControlDataRow
): HalfYearlyRecurrenceRule {
    const control = row.control.resource;
    if (control.scheduling?.rule?.type === "HalfYearly") {
        return control.scheduling.rule;
    } else {
        throw new Error(
            `Control(id=${control.id}) is expected to have HalfYearly recurrence rule`
        );
    }
}

function extractDueDate(row: ControlDataRow): Date | null {
    const scheduling = row.control.resource.scheduling;
    return scheduling ? parseDate(scheduling.startDate) : null;
}

function extractNextDueDate(row: ControlDataRow): Date | null {
    const nextDueDate = row.control.resource.nextDueDate;
    return nextDueDate ? parseDate(nextDueDate) : null;
}

function formatDueDate(row: ControlDataRow): string {
    const dueDate = extractDueDate(row);
    return dueDate ? formatDate(dueDate) : "";
}

function formatNextDueDate(row: ControlDataRow): string {
    const nextDueDate = extractNextDueDate(row);
    return nextDueDate ? formatDate(nextDueDate) : "";
}

const COMMON_ONLY_SEARCH_COLUMNS: Array<string> = [
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ASSIGNEE,
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_FIRST_REVIEWER,
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_SECOND_REVIEWER,
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY,
    CTRL_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
    ASSIGNEE_TAG_CATEGORIES_ID,
];

const COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR: Array<string> = [
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ASSIGNEE,
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_FIRST_REVIEWER,
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_SECOND_REVIEWER,
    ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY,
    CTRL_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
    ASSIGNEE_CATEGORY_EXTRACTORS_ID,
    ASSIGNEE_TAG_CATEGORIES_ID,
];

const extractorIds: { [key in SchedulingTypeOrAll]: Array<string> } = {
    All: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.SCHEDULING_TYPE,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
    Weekly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_WEEKLY,
        "onlySearchRecurrenceWeeklyDays",
        "onlySearchRecurrenceWeeklyPeriodicity",
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
    Monthly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_MONTHLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_MONTHLY,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
    EveryTwoMonths: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_EVERY_TWO_MONTHS,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_EVERY_TWO_MONTHS,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
    Quarterly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_QUARTERLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_QUARTERLY,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
    HalfYearly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_HALF_YEARLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_HALF_YEARLY,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
    Yearly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_YEARLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
    Manual: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        // There is no next due date, because due date manual covers it
        CTRL_EXTRACTOR_KEYS.DUE_DATE_MANUAL,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        TAG_CATEGORIES_ID,
        CATEGORY_EXTRACTORS_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS_WITH_EXTRACTOR,
    ],
};

const columnOrders: { [key in SchedulingTypeOrAll]: Array<string> } = {
    All: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.SCHEDULING_TYPE,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
    Weekly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_WEEKLY,
        "onlySearchRecurrenceWeeklyDays",
        "onlySearchRecurrenceWeeklyPeriodicity",
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
    Monthly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_MONTHLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_MONTHLY,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
    EveryTwoMonths: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_EVERY_TWO_MONTHS,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_EVERY_TWO_MONTHS,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
    Quarterly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_QUARTERLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_QUARTERLY,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
    HalfYearly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_HALF_YEARLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.SCHEDULE_HALF_YEARLY,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
    Yearly: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.RECURRENCE_YEARLY,
        CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
    Manual: [
        CTRL_EXTRACTOR_KEYS.NAME,
        CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        CTRL_EXTRACTOR_KEYS.PROGRAM,
        CTRL_EXTRACTOR_KEYS.DUE_DATE_MANUAL,
        CTRL_EXTRACTOR_KEYS.STATUS,
        CTRL_EXTRACTOR_KEYS.ASSIGNMENT,
        CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        CTRL_EXTRACTOR_KEYS.TAGS,
        TAG_CATEGORIES_ID,
        CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        ...COMMON_ONLY_SEARCH_COLUMNS,
    ],
};

function getColumns(
    type: SchedulingTypeOrAll,
    orgFinancialYear: FinancialYearView | null,
    calendars: CalendarView[],
    allTags: UserTagOverviewListDto,
    controls: ControlWithoutFieldsListGraphDto,
    usersById: Map<number, UserOverviewDtoContent>,
    userGroupById: Map<UserGroupId, UserGroupViewDtoContent>,
    controlEntitiesEnabled = false
): Array<DataExtractor<ControlDataRow, unknown>> {
    createColumns(
        orgFinancialYear,
        calendars,
        allTags,
        controls,
        usersById,
        userGroupById
    );

    let ids = columnOrders[type];
    if (!orgFinancialYear) {
        ids = ids.filter((id) => id !== CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR);
    }
    if (!controlEntitiesEnabled) {
        ids = ids.filter(
            (id) => id !== ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY
        );
    }
    return ids.map((id) => allColumns[id]);
}

function getExtractorIds(
    type: SchedulingTypeOrAll,
    hasFinancialYear: boolean,
    controlEntitiesEnabled = false
): Array<string> {
    let ids = extractorIds[type];
    if (!hasFinancialYear) {
        ids = ids.filter((id) => id !== CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR);
    }
    if (!controlEntitiesEnabled) {
        ids = ids.filter(
            (id) => id !== ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY
        );
    }
    return ids;
}

const columnBuilders: {
    [extractorId: string]: ColumnBuilder<ControlDataRow, any>;
} = {
    [DEFAULT_COLUMN_BUILDER_ID]: (extractor) => {
        if (
            DEFAULT_CATEGORY_METHODS.isCategoryExtractorId(extractor.id) ||
            isTagCategoriesExtractorId(extractor.id) ||
            isOnlySearchColumn(extractor.id) ||
            ASSIGNEE_CATEGORY_METHODS.isCategoryExtractorId(extractor.id) ||
            [
                ASSIGNEE_TAG_CATEGORIES_ID,
                CTRL_EXTRACTOR_KEYS.EXPORT_CONTROL_ID,
                CTRL_EXTRACTOR_KEYS.EXPORT_CONTROL_PROGRAM_ID,
            ].includes(extractor.id)
        ) {
            return {
                display: "NEVER",
            };
        }
        if (
            extractor.id === CTRL_EXTRACTOR_KEYS.STATUS ||
            extractor.id === CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION ||
            extractor.id === CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES ||
            extractor.id === CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR
        ) {
            return toColumn(extractor, {
                display: "DEFAULT_HIDDEN",
            });
        }
        return toColumn(extractor, {
            display: "DEFAULT_VISIBLE",
        });
    },
    [CTRL_EXTRACTOR_KEYS.NAME]: (_extractor) => ({
        getFormattedValue: (row) => {
            return (
                <ProtectedLink
                    href={`${WebRoute.CONTROLS}/${row.control.resource.id}`}
                    hasAccess={row.control.actions.view}
                >
                    {row.control.resource.title}
                </ProtectedLink>
            );
        },
        layout: { fixedWidth: 110 },
    }),
    [CTRL_EXTRACTOR_KEYS.DESCRIPTION]: (_extractor) => ({
        getFormattedValue: (row) => {
            const description = row.control.resource.description ?? "";
            let title;
            let truncated = description;

            if (description.length > MAX_DESCRIPTION_LENGTH) {
                // Minus one, so that ellipsis can be added
                truncated =
                    description.substring(0, MAX_DESCRIPTION_LENGTH - 1) + "…";
                title = description;
            }

            const markdownTitle = renderMarkdown(title);
            const markdown = renderMarkdown(truncated);

            // Wrapping in span in order for tooltip to anchor the cell
            return (
                <span>
                    <Tooltip
                        overlayClassName={theme.tooltipOverlay}
                        title={markdownTitle}
                        placement="left"
                    >
                        {markdown}
                    </Tooltip>
                </span>
            );
        },
        display: "DEFAULT_HIDDEN",
        layout: { fixedWidth: 200 },
    }),
    [CTRL_EXTRACTOR_KEYS.PROGRAM]: (_extractor) => ({
        getFormattedValue: (row) => {
            return (
                <ProtectedLink
                    href={buildControlProgramViewUrl(
                        row.controlProgram.resource.id
                    )}
                    hasAccess={row.controlProgram.actions.view}
                >
                    {row.controlProgram.resource.name}
                </ProtectedLink>
            );
        },
        layout: { fixedWidth: 160 },
    }),
    [CTRL_EXTRACTOR_KEYS.ASSIGNMENT]: (_extractor) => ({
        getFormattedValue: (row) => {
            const assigneeCount = row.assignments.length;
            return <span>{assigneeCount}</span>;
        },
        layout: { fixedWidth: 110 },
    }),
    [CTRL_EXTRACTOR_KEYS.TAGS]: (_extractor) => ({
        getFormattedValue: (row) => {
            return <TagList mode="HOVER" {...row.tags} />;
        },
        layout: { fixedWidth: 110 },
    }),
    [CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS]: (_extractor) => ({
        getFormattedValue: (row) => {
            return row.accessRight ? (
                <div className={theme.accessRightTooltipContainerDiv}>
                    {formatAccessRight(row.accessRight)}
                    <span className={theme.accessRightTooltipContainer}>
                        <InfoTooltip
                            text={getLocalizedAccessRightDescription(
                                ControlProgramAccessRightsInstance,
                                row.accessRight
                            )}
                        />
                    </span>
                </div>
            ) : null;
        },
        layout: { fixedWidth: 130 },
    }),
    [CTRL_EXTRACTOR_KEYS.RECURRENCE_WEEKLY]: (_extractor) => ({
        getFormattedValue: (row) => {
            const rule = extractWeeklyRecurrenceRule(row);
            const translatedDays = rule.days.map(translateDayOfWeek);
            return (
                <div>
                    <HoverableItemList
                        items={translatedDays}
                        label={t("Control_Scheduling_Day", {
                            count: translatedDays.length,
                        })}
                    />
                    <span>
                        {`${t("CreateControl_Reccurence_Weekly_Every")} ${t(
                            `CreateControl_Reccurence_Weekly_Periodicity_${rule.periodicity}` as AllowedPeriodicityTKeys
                        )}`}
                    </span>
                </div>
            );
        },
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.SCHEDULE_MONTHLY]: (_extractor) => ({
        getFormattedValue: (row) => {
            const translatedMonths =
                extractMonthlyRecurrenceRuleMonths(row).map(translateMonth);
            if (translatedMonths.length === 12) {
                return <div>{t("Controls_Schedule_AllMonths")}</div>;
            } else {
                return (
                    <div>
                        <HoverableItemList
                            items={translatedMonths}
                            label={t("Control_Scheduling_Month", {
                                count: translatedMonths.length,
                            })}
                        />
                    </div>
                );
            }
        },
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.SCHEDULE_EVERY_TWO_MONTHS]: (_extractor) => ({
        getFormattedValue: (row) => {
            const translatedPeriods =
                extractEveryTwoMonthsRecurrenceRulePeriods(row).map(
                    translateTwoMonthsPeriod
                );
            if (translatedPeriods.length === 6) {
                return <div>{t("Controls_Schedule_AllPeriods")}</div>;
            } else {
                return (
                    <div>
                        <HoverableItemList
                            items={translatedPeriods}
                            label={t("Control_Scheduling_Period", {
                                count: translatedPeriods.length,
                            })}
                        />
                    </div>
                );
            }
        },
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.SCHEDULE_QUARTERLY]: (_extractor) => ({
        getFormattedValue: (row) => {
            const translatedQuarters =
                extractQuarterlyRecurrenceRuleQuarters(row).map(
                    translateQuarter
                );
            if (translatedQuarters.length === 4) {
                return <div>{t("Controls_Schedule_AllQuarters")}</div>;
            } else {
                return (
                    <div>
                        <HoverableItemList
                            items={translatedQuarters}
                            label={t("Control_Scheduling_Quarter", {
                                count: translatedQuarters.length,
                            })}
                        />
                    </div>
                );
            }
        },
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.SCHEDULE_HALF_YEARLY]: (_extractor) => ({
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.DUE_DATE_MANUAL]: (_extractor) => ({
        getFormattedValue: (row) => formatDueDate(row),
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.NOTIFICATION]: (_extractor) => ({
        layout: { fixedWidth: 110 },
    }),
    [CTRL_EXTRACTOR_KEYS.RECURRENCE_MONTHLY]: (_extractor) => ({
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.RECURRENCE_EVERY_TWO_MONTHS]: (_extractor) => ({
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.RECURRENCE_QUARTERLY]: (_extractor) => ({
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.RECURRENCE_HALF_YEARLY]: (_extractor) => ({
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.RECURRENCE_YEARLY]: (_extractor) => ({
        layout: { fixedWidth: 150 },
    }),
    [CTRL_EXTRACTOR_KEYS.STATUS]: (_extractor) => ({
        layout: { fixedWidth: 90 },
    }),
    [CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION]: (_extractor) => ({
        layout: { fixedWidth: 160 },
    }),
    [CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES]: (_extractor) => ({
        layout: { fixedWidth: 180 },
    }),
    [CTRL_EXTRACTOR_KEYS.SCHEDULING_TYPE]: (_extractor) => ({
        layout: { fixedWidth: 140 },
    }),
    [CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE]: (_extractor) => ({
        getFormattedValue: (row) => formatNextDueDate(row),
        layout: { fixedWidth: 150 },
    }),
};

function buildControlColumnBuilders(
    buildActivitiesUrl: (controlId: string) => string
): {
    [extractorId: string]: ColumnBuilder<ControlDataRow, any>;
} {
    return {
        ...columnBuilders,
        [CTRL_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT]: (_extractor) => ({
            getFormattedValue: (row) => (
                <ProtectedLink
                    href={buildActivitiesUrl(row.control.resource.id)}
                    hasAccess={row.control.actions.view}
                >
                    {row.control.resource.controlResultCount}
                </ProtectedLink>
            ),
            layout: { fixedWidth: 110 },
        }),
    };
}

function formatHalfYearSchedule(halfs: HalfYear[]): string {
    switch (halfs.length) {
        case 0:
            return t("Half_None");
        case 1:
            return translateHalfYear(halfs[0]);
        case 2:
            return t("Half_Both");
        default:
            throw new Error(
                "HalfYearly control is not expected to have more than 2 occurrences"
            );
    }
}

function buildNotificationExtractor(): DataExtractor<ControlDataRow, number> {
    function formatNotification(row: ControlDataRow): string {
        const count = extractNotificationValue(row);
        return formatNotificationValue(count);
    }
    function extractNotificationValue(row: ControlDataRow): number {
        return row.control.resource.stepGroup.initialStep.allocatedTime.days;
    }
    function formatNotificationValue(daysCount: number): string {
        return t("Controls_Schedule_DayBefore", { count: daysCount });
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.NOTIFICATION,
        label: t("CreateControl_Recurrence_Notification_Label"),
        getValue: extractNotificationValue,
        compare: Cmp.map((row) => formatNotification(row), Cmp.string()),
        asString: (row) =>
            formatNotificationValue(extractNotificationValue(row)),
        customFilterTransformer: {
            getValue: extractNotificationValue,
            asString: formatNotificationValue,
        },
    };
}

function buildFinancialYearExtractor(
    orgFinancialYear: FinancialYearView | null
): DataExtractor<ControlDataRow, string, number | null> {
    function extractFinancialYearId(row: ControlDataRow): number | null {
        return row.financialYear?.id || null;
    }

    function formatNullableFinancialYear(
        financialYear: FinancialYearView | null
    ): string {
        return financialYear ? formatFinancialYear(financialYear) : "";
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.FINANCIAL_YEAR,
        label: t("CreateControl_Recurrence_FinancialYear"),
        getValue: (row) => formatNullableFinancialYear(row.financialYear),
        compare: Cmp.map(
            (row) => formatNullableFinancialYear(row.financialYear),
            Cmp.string()
        ),
        asString: (row) => formatNullableFinancialYear(row.financialYear),
        customFilterTransformer: {
            getValue: extractFinancialYearId,
            asString: (financialYearId: number | null) => {
                if (financialYearId === null) {
                    return "";
                } else if (financialYearId === orgFinancialYear?.id) {
                    return formatNullableFinancialYear(orgFinancialYear);
                } else {
                    throw new Error(
                        `Filter has financialYearId = ${financialYearId}, but orgFinancialYear is: ${JSON.stringify(
                            orgFinancialYear
                        )}`
                    );
                }
            },
        },
    };
}

function buildSchedulingTypeExtractor(): DataExtractor<
    ControlDataRow,
    ControlSchedulingType | null,
    SchedulingType | null
> {
    function extractControlSchedulingType(
        row: ControlDataRow
    ): ControlSchedulingType | null {
        return row.control.resource.scheduling?.rule?.type || null;
    }

    function extractSchedulingType(row: ControlDataRow): SchedulingType | null {
        const controlSchedulingType = extractControlSchedulingType(row);
        return controlSchedulingType
            ? controlSchedulingTypeToSchedulingType(controlSchedulingType)
            : null;
    }

    function formatControlSchedulingTypeNullable(
        typ: ControlSchedulingType | null,
        t: TFunction
    ): string {
        return typ ? formatControlSchedulingType(typ, t) : "";
    }

    function schedulingTypeNullableToControlSchedulingType(
        typ: SchedulingType | null
    ): ControlSchedulingType | null {
        return typ ? schedulingTypeToControlSchedulingType(typ) : null;
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.SCHEDULING_TYPE,
        label: t("SchedulingType"),
        getValue: extractControlSchedulingType,
        compare: Cmp.map(
            extractControlSchedulingType,
            Cmp.undefinedLast(Cmp.string())
        ),
        asString: (row) =>
            formatControlSchedulingTypeNullable(
                extractControlSchedulingType(row),
                t
            ),
        customFilterTransformer: {
            getValue: extractSchedulingType,
            asString: (schedulingType: SchedulingType | null) =>
                formatControlSchedulingTypeNullable(
                    schedulingTypeNullableToControlSchedulingType(
                        schedulingType
                    ),
                    t
                ),
        },
    };
}

function buildControlResultsCountExtractor(): DataExtractor<
    ControlDataRow,
    number
> {
    const getCount = (row: ControlDataRow) =>
        row.control.resource.controlResultCount;

    return {
        id: CTRL_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
        label: t("ControlResultsCount"),
        getValue: getCount,
        compare: Cmp.map(getCount, Cmp.primitive),
        asString: (row) => getCount(row).toLocaleString(),
        customFilterTransformer: {
            getValue: getCount,
            asString: (val) => val.toLocaleString(),
        },
    };
}

function buildStatusExtractor(): DataExtractor<
    ControlDataRow,
    ControlProgramStatus,
    boolean
> {
    function extractRunning(row: ControlDataRow): boolean {
        return row.controlProgram.resource.running;
    }

    function boolToStatus(running: boolean): ControlProgramStatus {
        return running ? "Active" : "Inactive";
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.STATUS,
        label: t("AccessAndAssignment_ControlPrograms_Status"),
        getValue: (row) => boolToStatus(extractRunning(row)),
        compare: Cmp.map(extractRunning, Cmp.primitive),
        asString: (row) =>
            formatControlProgramStatus(boolToStatus(extractRunning(row))),
        customFilterTransformer: {
            getValue: extractRunning,
            asString: (isRunning) =>
                formatControlProgramStatus(boolToStatus(isRunning)),
        },
    };
}

function buildLoginProtectionExtractor(): DataExtractor<
    ControlDataRow,
    ControlLoginProtection,
    boolean
> {
    function extractLoginProtection(row: ControlDataRow): boolean {
        return row.control.resource.assignmentSettings.loginProtectionEnabled;
    }
    function boolToControlLoginProtection(
        isEnabled: boolean
    ): ControlLoginProtection {
        return isEnabled ? "Required" : "NotRequired";
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.LOGIN_PROTECTION,
        label: t("CreateControl_LoginProtectionLabel"),
        getValue: (row) =>
            boolToControlLoginProtection(extractLoginProtection(row)),
        compare: Cmp.map(extractLoginProtection, Cmp.primitive),
        asString: (row) =>
            formatControlLoginProtection(
                boolToControlLoginProtection(extractLoginProtection(row))
            ),
        customFilterTransformer: {
            getValue: extractLoginProtection,
            asString: (isEnabled: boolean) =>
                formatControlLoginProtection(
                    boolToControlLoginProtection(isEnabled)
                ),
        },
    };
}

function buildSegregationOfDutiesExtractor(): DataExtractor<
    ControlDataRow,
    ControlSegregationOfDuties,
    boolean
> {
    function extractSegregationOfDutiesEnabled(row: ControlDataRow): boolean {
        return row.control.resource.assignmentSettings
            .segregationOfDutiesEnabled;
    }

    function boolToControlSegregationOfDuties(
        isEnabled: boolean
    ): ControlSegregationOfDuties {
        return isEnabled ? "Enforced" : "NotEnforced";
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.SEGREGATION_OF_DUTIES,
        label: t("SegregationOfDuties"),
        getValue: (row) =>
            boolToControlSegregationOfDuties(
                extractSegregationOfDutiesEnabled(row)
            ),
        compare: Cmp.map(extractSegregationOfDutiesEnabled, Cmp.primitive),
        asString: (row) =>
            formatControlSegregationOfDuties(
                boolToControlSegregationOfDuties(
                    extractSegregationOfDutiesEnabled(row)
                )
            ),
        customFilterTransformer: {
            getValue: extractSegregationOfDutiesEnabled,
            asString: (isEnabled) =>
                formatControlSegregationOfDuties(
                    boolToControlSegregationOfDuties(isEnabled)
                ),
        },
    };
}

function buildAccessRightExtractor(): DataExtractor<
    ControlDataRow,
    StandardAccessRight | null
> {
    return {
        id: CTRL_EXTRACTOR_KEYS.ACCESS_RIGHTS,
        label: t("Column_Access"),
        getValue: (row) => row.accessRight,
        compare: Cmp.map(
            (row) => row.accessRight,
            Cmp.undefinedLast(compareAccessRight)
        ),
        asString: (row) =>
            row.accessRight ? formatAccessRight(row.accessRight) : "",
        customFilterTransformer: {
            getValue: (row) => row.accessRight,
            asString: (accessRight) =>
                accessRight ? formatAccessRight(accessRight) : "",
        },
    };
}

function buildOnlySearchRecurrenceWeeklyDaysExtractor(): DataExtractor<
    ControlDataRow,
    string,
    DayOfWeek[]
> {
    return {
        id: "onlySearchRecurrenceWeeklyDays",
        label:
            t("CreateControl_Reccurence_TypeLabel") +
            ": " +
            t("CreateControl_Recurrence_DateRule_Days"),
        getValue: (_row) => "",
        compare: (_a, _b) => 0,
        asString: (row) =>
            extractWeeklyRecurrenceRule(row)
                .days.map(translateDayOfWeek)
                .join(", "),
        customFilterTransformer: {
            getValue: (row) => extractWeeklyRecurrenceRule(row).days,
            asString: (days: DayOfWeek[]) => {
                return days.map(translateDayOfWeek).join(", ");
            },
        },
    };
}

function buildOnlySearchRecurrenceWeeklyPeriodicityExtractor(): DataExtractor<
    ControlDataRow,
    string,
    number
> {
    return {
        id: "onlySearchRecurrenceWeeklyPeriodicity",
        label:
            t("CreateControl_Reccurence_TypeLabel") +
            ": " +
            t("CreateControl_Reccurence_TypeLabel"),
        getValue: (_row) => "",
        compare: (_a, _b) => 0,
        asString: (row) =>
            `${t(
                "CreateControl_Reccurence_Weekly_Every"
            )} ${translateRecurrenceWeeklyPeriodicity(
                extractWeeklyRecurrenceRule(row).periodicity
            )}`,
        customFilterTransformer: {
            getValue: (row) => extractWeeklyRecurrenceRule(row).periodicity,
            asString: translateRecurrenceWeeklyPeriodicity,
        },
    };
}

function formatWeeklyRecurrence(row: ControlDataRow): string {
    const rule = extractWeeklyRecurrenceRule(row);
    const daysOfWeek = rule.days.map(translateDayOfWeek).join(", ");
    const periodicity = translateRecurrenceWeeklyPeriodicity(rule.periodicity);

    return t("Controls_Export_Recurrence_Weekly", {
        daysOfWeek,
        periodicity,
    });
}

function buildScheduleMonthlyExtractor(): DataExtractor<
    ControlDataRow,
    string,
    Array<Month>
> {
    function formatMonths(months: Month[]): string {
        if (months.length === 12) {
            return t("Controls_Schedule_AllMonths");
        } else {
            return months.map(translateMonth).join(", ");
        }
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.SCHEDULE_MONTHLY,
        label: t("CreateControl_Scheduling_Title"),
        getValue: (_row) => "",
        compare: (a, b) =>
            Object.keys(extractMonthlyRecurrenceRule(a).occurrences).length -
            Object.keys(extractMonthlyRecurrenceRule(b).occurrences).length,
        asString: (row) =>
            formatMonths(extractMonthlyRecurrenceRuleMonths(row)),
        customFilterTransformer: {
            getValue: extractMonthlyRecurrenceRuleMonths,
            asString: formatMonths,
        },
    };
}

function buildRecurrenceMonthlyExtractor(
    calendars: CalendarView[]
): DataExtractor<ControlDataRow, string, Recurrence> {
    function formatRecurrenceMonthly(row: ControlDataRow): string {
        return formatRecurrenceWithEnding(row, "Month");
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.RECURRENCE_MONTHLY,
        label: t("CreateControl_Reccurence_TypeLabel"),
        getValue: (row) => formatRecurrenceMonthly(row),
        compare: Cmp.map((row) => formatRecurrenceMonthly(row), Cmp.string()),
        asString: (row) => formatRecurrenceMonthly(row),
        customFilterTransformer: buildCustomFilterTransformerForRecurrence(
            "Month",
            calendars
        ),
    };
}

function buildRecurrenceEveryTwoMonthsExtractor(
    calendars: CalendarView[]
): DataExtractor<ControlDataRow, string, Recurrence> {
    function formatRecurrenceTwoMonths(row: ControlDataRow): string {
        return formatRecurrenceWithEnding(row, "TwoMonths");
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.RECURRENCE_EVERY_TWO_MONTHS,
        label: t("CreateControl_Reccurence_TypeLabel"),
        getValue: (row) => formatRecurrenceTwoMonths(row),
        compare: Cmp.map((row) => formatRecurrenceTwoMonths(row), Cmp.string()),
        asString: (row) => formatRecurrenceTwoMonths(row),
        customFilterTransformer: buildCustomFilterTransformerForRecurrence(
            "TwoMonths",
            calendars
        ),
    };
}

function buildRecurrenceQuarterlyExtractor(
    calendars: CalendarView[]
): DataExtractor<ControlDataRow, string, Recurrence> {
    function formatRecurrenceQuarterly(row: ControlDataRow): string {
        return formatRecurrenceWithEnding(row, "Quarter");
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.RECURRENCE_QUARTERLY,
        label: t("CreateControl_Reccurence_TypeLabel"),
        getValue: (row) => formatRecurrenceQuarterly(row),
        compare: Cmp.map((row) => formatRecurrenceQuarterly(row), Cmp.string()),
        asString: (row) => formatRecurrenceQuarterly(row),
        customFilterTransformer: buildCustomFilterTransformerForRecurrence(
            "Quarter",
            calendars
        ),
    };
}

function buildRecurrenceHalfYearlyExtractor(
    calendars: CalendarView[]
): DataExtractor<ControlDataRow, string, Recurrence> {
    function formatRecurrenceHalfYearly(row: ControlDataRow): string {
        return formatRecurrenceWithEnding(row, "Half");
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.RECURRENCE_HALF_YEARLY,
        label: t("CreateControl_Reccurence_TypeLabel"),
        getValue: (row) => formatRecurrenceHalfYearly(row),
        compare: Cmp.map(
            (row) => formatRecurrenceHalfYearly(row),
            Cmp.string()
        ),
        asString: (row) => formatRecurrenceHalfYearly(row),
        customFilterTransformer: buildCustomFilterTransformerForRecurrence(
            "Half",
            calendars
        ),
    };
}

function buildRecurrenceYearlyExtractor(
    calendars: CalendarView[]
): DataExtractor<ControlDataRow, string, Recurrence> {
    function formatRecurrenceYearly(row: ControlDataRow): string {
        return formatRecurrenceWithEnding(row, "Year");
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.RECURRENCE_YEARLY,
        label: t("CreateControl_Reccurence_TypeLabel"),
        getValue: (row) => formatRecurrenceYearly(row),
        compare: Cmp.map((row) => formatRecurrenceYearly(row), Cmp.string()),
        asString: (row) => formatRecurrenceYearly(row),
        customFilterTransformer: buildCustomFilterTransformerForRecurrence(
            "Year",
            calendars
        ),
    };
}

function buildCustomFilterTransformerForRecurrence(
    ending: PeriodEnding,
    calendars: CalendarView[]
): CustomFilterTransformer<ControlDataRow, Recurrence> {
    function formatRecurrenceWithEndingFromValue(
        recurrence: Recurrence,
        calendars: CalendarView[],
        ending: PeriodEnding
    ): string {
        const { calendarId, defaultDueDateOffset } = recurrence;
        const calendar = calendars.find((c) => c.id === calendarId);
        return formatRecurrenceCanonical(
            defaultDueDateOffset,
            calendar,
            ending
        );
    }

    return {
        getValue: extractRecurrence,
        asString: (recurrence: Recurrence) =>
            formatRecurrenceWithEndingFromValue(recurrence, calendars, ending),
    };
}

function extractRecurrence(row: ControlDataRow): Recurrence {
    const defaultDueDateOffset = extractDefaultDueDateOffset(row);
    const calendarId = row.calendar ? row.calendar.id : undefined;
    return {
        defaultDueDateOffset,
        calendarId,
    };
}

function buildScheduleEveryTwoMonthsExtractor(): DataExtractor<
    ControlDataRow,
    string,
    TwoMonths[]
> {
    function formatTwoMonthsPeriods(periods: TwoMonths[]): string {
        if (periods.length === 6) {
            return t("Controls_Schedule_AllPeriods");
        } else {
            return periods.map(translateTwoMonthsPeriod).join(", ");
        }
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.SCHEDULE_EVERY_TWO_MONTHS,
        label: t("CreateControl_Scheduling_Title"),
        getValue: (_row) => "",
        compare: (a, b) =>
            Object.keys(extractEveryTwoMonthsRecurrenceRule(a).occurrences)
                .length -
            Object.keys(extractEveryTwoMonthsRecurrenceRule(b).occurrences)
                .length,
        asString: (row) =>
            formatTwoMonthsPeriods(
                extractEveryTwoMonthsRecurrenceRulePeriods(row)
            ),
        customFilterTransformer: {
            getValue: extractEveryTwoMonthsRecurrenceRulePeriods,
            asString: formatTwoMonthsPeriods,
        },
    };
}

function buildScheduleQuarterlyExtractor(): DataExtractor<
    ControlDataRow,
    string,
    Quarter[]
> {
    function formatQuarters(quarters: Quarter[]): string {
        if (quarters.length === 4) {
            return t("Controls_Schedule_AllQuarters");
        } else {
            return quarters.map(translateQuarter).join(", ");
        }
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.SCHEDULE_QUARTERLY,
        label: t("CreateControl_Scheduling_Title"),
        getValue: (_row) => "",
        compare: (a, b) =>
            Object.keys(extractQuarterlyRecurrenceRule(a).occurrences).length -
            Object.keys(extractQuarterlyRecurrenceRule(b).occurrences).length,
        asString: (row) =>
            formatQuarters(extractQuarterlyRecurrenceRuleQuarters(row)),
        customFilterTransformer: {
            getValue: extractQuarterlyRecurrenceRuleQuarters,
            asString: formatQuarters,
        },
    };
}

function buildScheduleHalfYearlyExtractor(): DataExtractor<
    ControlDataRow,
    string,
    HalfYear[]
> {
    function extractHalfYearlyRecurrenceRuleHalfs(
        row: ControlDataRow
    ): HalfYear[] {
        const rule = extractHalfYearlyRecurrenceRule(row);
        // SAFETY: rule.occurrences is of type Record<HalfYear, MonthDueDateOffset>.
        return Object.keys(rule.occurrences) as Array<HalfYear>;
    }

    return {
        id: CTRL_EXTRACTOR_KEYS.SCHEDULE_HALF_YEARLY,
        label: t("CreateControl_Scheduling_Title"),
        getValue: (_row) => "",
        compare: (a, b) =>
            Object.keys(extractHalfYearlyRecurrenceRule(a).occurrences).length -
            Object.keys(extractHalfYearlyRecurrenceRule(b).occurrences).length,
        asString: (row) =>
            formatHalfYearSchedule(extractHalfYearlyRecurrenceRuleHalfs(row)),
        customFilterTransformer: {
            getValue: extractHalfYearlyRecurrenceRuleHalfs,
            asString: formatHalfYearSchedule,
        },
    };
}

function buildDueDateManualExtractor(): DataExtractor<
    ControlDataRow,
    Date | null
> {
    return {
        id: CTRL_EXTRACTOR_KEYS.DUE_DATE_MANUAL,
        label: t("ControlResult_DueDate"),
        getValue: (row) => extractDueDate(row),
        compare: Cmp.map(
            (row) => extractDueDate(row),
            Cmp.undefinedLast(Cmp.date)
        ),
        asString: (row) => formatDueDate(row),
        customFilterTransformer: {
            getValue: extractDueDate,
            asString: (date: Date | null) => (date ? formatDate(date) : ""),
        },
    };
}

function buildNextDueDateExtractor(): DataExtractor<
    ControlDataRow,
    Date | null
> {
    return {
        id: CTRL_EXTRACTOR_KEYS.NEXT_DUE_DATE,
        label: t("ControlResult_NextDueDate"),
        getValue: (row) => extractNextDueDate(row),
        compare: Cmp.map(
            (row) => extractNextDueDate(row),
            Cmp.undefinedLast(Cmp.date)
        ),
        asString: (row) => formatNextDueDate(row),
        customFilterTransformer: {
            getValue: extractNextDueDate,
            asString: (date: Date | null) => (date ? formatDate(date) : ""),
        },
    };
}

function buildTagCategoriesExtractor(
    allTags: UserTagOverviewListDto
): DataExtractor<ControlDataRow, TagsData, IdAndName<string, string>> {
    const categoryNameLookup = createCategoryNameLookupFn(allTags);

    return {
        id: TAG_CATEGORIES_ID,
        label: t("Search_Tag_Categories"),
        getValue: (row) => row.tags,
        compare: (a, b) =>
            a.tags.tagCategories.length - b.tags.tagCategories.length,
        asString: (_row) => "",
        customFilterTransformer: {
            getValue: (_row) => {
                // Every control row has multiple tag categories.
                // For this case customFilterTransformer.getValue of expander is used.
                // See definition of buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds()
                throw new Error(
                    "Unexpected call of customFilterTransformer.getValue() on tagCategories extractor."
                );
            },
            asString: categoryNameLookup,
        },
    };
}

function buildTagsExtractor(
    allTags: UserTagOverviewListDto
): DataExtractor<ControlDataRow, TagsData, IdAndName<string, string>> {
    const tagNameLookup = createTagNameLookupFn(allTags);

    return {
        id: CTRL_EXTRACTOR_KEYS.TAGS,
        label: t("Tags"),
        getValue: (row) => row.tags,
        compare: Cmp.map((row) => row.tags.tags.length, Cmp.primitive),
        asString: (_row) => {
            throw new Error("Unexpected call of asString() on tags extractor.");
        },
        customFilterTransformer: {
            getValue: (_row) => {
                throw new Error(
                    "Unexpected call of customFilterTransformer.getValue() on tags extractor."
                );
            },
            asString: tagNameLookup,
        },
    };
}

function buildNameExtractor(
    controls: ControlWithoutFieldsListGraphDto
): DataExtractor<ControlDataRow, IdAndName<string, string>> {
    // lookupControlName() is not based on ALL existing controls, but on the controls that were fetched in the current context (filtered by schedulingType).
    // But it does not have effect on the filtering results, because the matching controls are not within the scope anyway.
    const idToNameMap: Record<string, string> = {};
    controls.content.forEach((controlDto) => {
        const { id, title } = controlDto.resource;
        idToNameMap[id] = title;
    });
    const lookupControlName = (
        idAndName: IdAndName<string, string>
    ): string => {
        return idToNameMap[idAndName.id] || idAndName.name;
    };

    const extractControlIdAndName = (row: ControlDataRow) => {
        const { id, title } = row.control.resource;
        return { id, name: title };
    };
    const formatControlName = (idAndName: IdAndName<string, string>) =>
        idAndName.name;

    return {
        id: CTRL_EXTRACTOR_KEYS.NAME,
        label: t("CreateControl_Control_Title"),
        getValue: extractControlIdAndName,
        compare: Cmp.map((row) => row.control.resource.title, Cmp.string()),
        asString: (row) => formatControlName(extractControlIdAndName(row)),
        customFilterTransformer: {
            getValue: extractControlIdAndName,
            asString: lookupControlName,
        },
    };
}

function buildDescriptionExtractor(
    _controls: ControlWithoutFieldsListGraphDto
): DataExtractor<ControlDataRow, string> {
    const extractDescription = (row: ControlDataRow) =>
        row.control.resource.description || undefined;

    const extractDescriptionString = (row: ControlDataRow) =>
        extractDescription(row) || "";

    return {
        id: CTRL_EXTRACTOR_KEYS.DESCRIPTION,
        label: t("Control_Description"),
        getValue: extractDescription,
        compare: Cmp.map(
            (row) => extractDescription(row),
            Cmp.undefinedLast(Cmp.string())
        ),
        asString: extractDescriptionString,
    };
}

function buildProgramExtractor(
    controls: ControlWithoutFieldsListGraphDto
): DataExtractor<ControlDataRow, IdAndName<number, string>> {
    // lookupControlName() is not based on ALL existing control programs, but on the controls and relevant programs that were fetched in the current context (filtered by schedulingType).
    // But it does not have effect on the filtering results, because the matching control programs are not within the scope anyway.
    const idToNameMap: Record<number, string> = {};
    controls.linkedResources.controlPrograms.forEach((cpDto) => {
        const { id, name } = cpDto.resource;
        idToNameMap[id] = name;
    });
    const lookupControlProgramName = (
        idAndName: IdAndName<number, string>
    ): string => {
        return idToNameMap[idAndName.id] || idAndName.name;
    };
    const extractControlProgramIdAndName = (row: ControlDataRow) => {
        const { id, name } = row.controlProgram.resource;
        return { id, name };
    };
    const formatControlProgramName = (idAndName: IdAndName<number, string>) =>
        idAndName.name;

    return {
        id: CTRL_EXTRACTOR_KEYS.PROGRAM,
        label: t("ControlProgram"),
        getValue: extractControlProgramIdAndName,
        compare: Cmp.map(
            (row) => row.controlProgram.resource.name,
            Cmp.string()
        ),
        asString: (row) =>
            formatControlProgramName(extractControlProgramIdAndName(row)),
        customFilterTransformer: {
            getValue: extractControlProgramIdAndName,
            asString: lookupControlProgramName,
        },
    };
}

function buildNameTransformerFn(
    usersById: Map<number, UserOverviewDtoContent>,
    usersGroupById: Map<UserGroupId, UserGroupViewDtoContent>
): (userOrGroup: UserOrGroup) => string {
    return (userOrGroup: UserOrGroup) => {
        switch (userOrGroup.type) {
            case "User":
                return formatUserName(
                    usersById.get(userOrGroup.value.id)?.resource ||
                        userOrGroup.value.name
                );
            case "Group":
                return (
                    usersGroupById.get(userOrGroup.value.id)?.resource.name ||
                    userOrGroup.value.name
                );
            default:
                return assertNever(userOrGroup);
        }
    };
}

function buildAssignmentsNameExtractor(
    id: string,
    label: string,
    whatAssignment: string,
    usersById: Map<number, UserOverviewDtoContent>,
    userGroupById: Map<UserGroupId, UserGroupViewDtoContent>
): DataExtractor<ControlDataRow, ControlAssignmentGroup[], UserOrGroup> {
    const nameTransformer = buildNameTransformerFn(usersById, userGroupById);

    return {
        id,
        label,
        getValue: (row) => row.assignments,
        compare: (_a, _b) => 0,
        asString: (_row) => {
            throw new Error(
                `Unexpected call of asString() on ${whatAssignment} name extractor.`
            );
        },
        customFilterTransformer: {
            getValue: (_row) => {
                throw new Error(
                    `Unexpected call of customFilterTransformer.getValue() on ${whatAssignment} name extractor.`
                );
            },
            asString: nameTransformer,
        },
    };
}

function buildInitialAssignmentNameExtractorExpander(
    extractorId: string,
    keyPrefix: string,
    usersById: Map<number, UserOverviewDtoContent>,
    userGroupById: Map<UserGroupId, UserGroupViewDtoContent>
): ExtractorExpander<ControlAssignmentGroup[], UserOrGroup> {
    const nameTransformer = buildNameTransformerFn(usersById, userGroupById);

    const result: ExtractorExpander<ControlAssignmentGroup[], UserOrGroup> = {
        extractorId,
        expand: (assignments: ControlAssignmentGroup[]) => {
            const results: Array<PartialData<UserOrGroup>> = [];
            assignments.forEach((row) => {
                const ass = row.initialAssignment;
                switch (ass.type) {
                    case "User": {
                        const user = usersById.get(ass.userId)?.resource;
                        if (user) {
                            results.push({
                                partialId: `${keyPrefix}_user-${ass.userId}`,
                                expandedValue: {
                                    type: "User",
                                    value: {
                                        id: ass.userId,
                                        name: {
                                            firstname: user.firstname,
                                            lastname: user.lastname,
                                        },
                                    },
                                },
                            });
                        }
                        break;
                    }
                    case "Group":
                    case "Pool": {
                        const name =
                            userGroupById.get(ass.userGroupId)?.resource.name ||
                            "";
                        results.push({
                            partialId: `${keyPrefix}_pool-${ass.userGroupId}`,
                            expandedValue: {
                                type: "Group",
                                value: { id: ass.userGroupId, name },
                            },
                        });
                        break;
                    }
                    default: {
                        assertNever(ass);
                    }
                }
            });
            return results;
        },
        compare: Cmp.undefinedLast((_a, _b) => 0),
        asString: nameTransformer,
        customFilterTransformer: {
            getValue: (item) => item,
            asString: nameTransformer,
        },
    };
    return result;
}

function buildSubsequentAssignmentNameExtractorExpander(
    extractorId: string,
    getAssignment: (
        assignment_group: ControlAssignmentGroup
    ) => SubsequentAssignmentKind | undefined,
    keyPrefix: string,
    usersById: Map<number, UserOverviewDtoContent>,
    userGroupById: Map<UserGroupId, UserGroupViewDtoContent>
): ExtractorExpander<ControlAssignmentGroup[], UserOrGroup> {
    const nameTransformer = buildNameTransformerFn(usersById, userGroupById);

    const result: ExtractorExpander<ControlAssignmentGroup[], UserOrGroup> = {
        extractorId,
        expand: (assignments: ControlAssignmentGroup[]) => {
            const results: Array<PartialData<UserOrGroup>> = [];
            assignments.forEach((row) => {
                const ass = getAssignment(row);
                switch (ass?.type) {
                    case undefined: {
                        break;
                    }
                    case "User": {
                        const user = usersById.get(ass.userId)?.resource;
                        if (user) {
                            results.push({
                                partialId: `${keyPrefix}_user-${ass.userId}`,
                                expandedValue: {
                                    type: "User",
                                    value: {
                                        id: ass.userId,
                                        name: {
                                            firstname: user.firstname,
                                            lastname: user.lastname,
                                        },
                                    },
                                },
                            });
                        }
                        break;
                    }
                    case "Pool": {
                        const name =
                            userGroupById.get(ass.userGroupId)?.resource.name ||
                            "";
                        results.push({
                            partialId: `${keyPrefix}_pool-${ass.userGroupId}`,
                            expandedValue: {
                                type: "Group",
                                value: { id: ass.userGroupId, name },
                            },
                        });
                        break;
                    }
                    default: {
                        assertNever(ass);
                    }
                }
            });
            return results;
        },
        compare: Cmp.undefinedLast((_a, _b) => 0),
        asString: nameTransformer,
        customFilterTransformer: {
            getValue: (item) => item,
            asString: nameTransformer,
        },
    };
    return result;
}

function buildAssigneeTagCategoriesExtractor(
    allTags: UserTagOverviewListDto
): DataExtractor<ControlDataRow, TagsData, IdAndName<string, string>> {
    const categoryNameLookup = createCategoryNameLookupFn(allTags);

    return {
        id: ASSIGNEE_TAG_CATEGORIES_ID,
        label: t("Control_Management_AssigneeTagCategories"),
        getValue: (row) => row.assigneeTags,
        compare: (a, b) =>
            a.assigneeTags.tagCategories.length -
            b.assigneeTags.tagCategories.length,
        asString: (_row) => "",
        customFilterTransformer: {
            getValue: (_row) => {
                // Every control row has multiple tag categories.
                // For this case customFilterTransformer.getValue of expander is used.
                // See definition of buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds()
                throw new Error(
                    "Unexpected call of customFilterTransformer.getValue() on ASSIGNEE_TAG_CATEGORIES_ID extractor."
                );
            },
            asString: categoryNameLookup,
        },
    };
}

function buildAssignmentsEntityExtractor(): DataExtractor<
    ControlDataRow,
    ControlAssignmentGroup[],
    IdAndName<number, string>
> {
    return {
        id: ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY,
        label: t("Control_Management_Entity"),
        getValue: (row) => row.assignments,
        compare: (_a, _b) => 0,
        asString: (_row) => {
            throw new Error(
                "Unexpected call of asString() on entity name extractor."
            );
        },
        customFilterTransformer: {
            getValue: (_row) => {
                throw new Error(
                    "Unexpected call of customFilterTransformer.getValue() on entity name extractor."
                );
            },
            asString: (idAndName: IdAndName<number, string>) => idAndName.name,
        },
    };
}

function buildEntityExtractorExpander(
    entitiesById: Map<number, EntityView>
): ExtractorExpander<ControlAssignmentGroup[], IdAndName<number, string>> {
    const nameTransformer = (idAndName: IdAndName<number, string>): string =>
        idAndName.name;

    return {
        extractorId: ASSIGNMENT_EXTRACTOR_KEYS.ONLY_SEARCH_ENTITY,
        expand: (assignments: ControlAssignmentGroup[]) => {
            const results: Array<PartialData<IdAndName<number, string>>> = [];
            const seen = new Set<number>();
            assignments.forEach((group) => {
                const entityId = group.entityId;
                if (entityId === undefined) {
                    return;
                }
                if (seen.has(entityId)) {
                    return;
                }
                seen.add(entityId);
                const entity = entitiesById.get(entityId);
                if (entity) {
                    results.push({
                        partialId: `entity-${entityId}`,
                        expandedValue: {
                            id: entity.id,
                            name: entity.name,
                        },
                    });
                }
            });
            return results;
        },
        compare: Cmp.undefinedLast((_a, _b) => 0),
        asString: nameTransformer,
        customFilterTransformer: {
            getValue: (item) => item,
            asString: nameTransformer,
        },
    };
}

interface ExportExtractors {
    id: DataExtractor<ControlDataRow, string, string>;
    programId: DataExtractor<ControlDataRow, number, number>;
}

export {
    type SchedulingTypeOrAll,
    getColumns,
    getExtractorIds,
    CATEGORY_EXTRACTORS_ID,
    buildControlColumnBuilders,
    buildInitialAssignmentNameExtractorExpander,
    buildSubsequentAssignmentNameExtractorExpander,
    buildEntityExtractorExpander,
    ASSIGNEE_CATEGORY_EXTRACTORS_ID,
    type ExportExtractors,
};
